import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  CalendarClock,
  ClipboardCheck,
  ExternalLink,
  RotateCcw,
  Sigma,
  Target,
  Timer,
} from "lucide-react";
import StudyShell from "@/components/study/StudyShell";
import { getStudyDashboard, getStudyPlan } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const PSI_BULLETIN_URL = "https://test-takers.psiexams.com/api/content/bulletin/6251";

const Action = ({ to, icon: Icon, title, detail }) => (
  <Link
    to={to}
    className="flex min-h-[112px] items-start justify-between gap-4 border-b border-[color:rgba(10,27,51,0.10)] bg-white px-5 py-5 transition-colors hover:bg-[var(--ibp-offwhite)]"
  >
    <span className="flex items-start gap-3">
      <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[var(--ibp-navy-950)] text-[var(--ibp-gold-500)]">
        <Icon className="h-5 w-5" />
      </span>
      <span>
        <span className="block text-sm font-semibold text-[var(--ibp-ink)]">{title}</span>
        <span className="mt-1 block text-xs leading-5 text-[color:rgba(11,18,32,0.62)]">
          {detail}
        </span>
      </span>
    </span>
    <ArrowRight className="mt-3 h-4 w-4 flex-none text-[color:rgba(11,18,32,0.42)]" />
  </Link>
);

export default function StudyExamWeek() {
  const { data: dashboard, isLoading: dashboardLoading } = useQuery({
    queryKey: ["study-dashboard"],
    queryFn: getStudyDashboard,
  });
  const { data: plan, isLoading: planLoading } = useQuery({
    queryKey: ["study-plan"],
    queryFn: getStudyPlan,
  });

  const isLoading = dashboardLoading || planLoading;
  const daysUntil = plan?.days_until_exam;
  const dateMessage =
    typeof daysUntil === "number"
      ? daysUntil < 0
        ? "Your saved exam date has passed. Update it when your next attempt is scheduled."
        : daysUntil === 0
        ? "Exam day is today. Keep the final review calm and focused."
        : `${daysUntil} day${daysUntil === 1 ? "" : "s"} until your saved exam date.`
      : "Add your exam date to anchor this final-review workspace.";

  return (
    <StudyShell testId={STUDY.examWeekPage}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[var(--ibp-gold-500)] text-[var(--ibp-ink)]">
          <CalendarClock className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#B7791F]">Final review</p>
          <h1 className="mt-1 text-2xl font-bold text-[var(--ibp-ink)]">Exam Week</h1>
          <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">{dateMessage}</p>
        </div>
      </div>

      {isLoading ? (
        <p className="mt-8 text-sm text-[color:rgba(11,18,32,0.62)]">Preparing your review...</p>
      ) : (
        <>
          {!plan?.exam_date ? (
            <div className="mt-6 flex flex-col gap-3 border-l-4 border-l-[var(--ibp-gold-500)] bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-[color:rgba(11,18,32,0.72)]">
                Set your exam date and choose the Final 48-Hour Cram plan when test day is close.
              </p>
              <Link
                to="/study/plan"
                className="inline-flex flex-none items-center gap-1.5 text-sm font-semibold text-[var(--ibp-navy-950)] hover:underline"
              >
                Set exam date <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : null}

          <section className="mt-8">
            <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Your final-review order</h2>
            <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.62)]">
              Clean up known weaknesses first, then use one exact-length simulation to check pacing.
            </p>
            <div className="mt-4 overflow-hidden border-y border-[color:rgba(10,27,51,0.12)]">
              <Action
                to="/study/missed"
                icon={RotateCcw}
                title={`Clear missed questions (${dashboard?.missed_count || 0})`}
                detail="Redo open misses until you can answer them correctly twice."
              />
              <Action
                to="/study/practice?portion=math"
                icon={Sigma}
                title="Run a focused math drill"
                detail="Refresh prorations, commissions, area, financing, and transfer calculations."
              />
              <Action
                to="/study/mini-exams/illinois-broker-simulation"
                icon={Target}
                title="Take the Illinois Broker simulation"
                detail="40 questions, 90 minutes, with a 75% practice target."
              />
              <Action
                to="/study/mini-exams/national-broker-simulation"
                icon={Timer}
                title="Take the National Broker simulation"
                detail="100 questions, 150 minutes, with a 70% practice target."
              />
            </div>
          </section>

          <section className="mt-8 grid gap-5 md:grid-cols-[1.1fr_0.9fr]">
            <div className="bg-[var(--ibp-navy-950)] p-6 text-white">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Target className="h-4 w-4 text-[var(--ibp-gold-500)]" /> Weakest topics
              </h2>
              {(dashboard?.weakest_topics || []).length ? (
                <ul className="mt-4 space-y-3 text-sm text-white/80">
                  {dashboard.weakest_topics.map((topic) => (
                    <li key={topic} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[var(--ibp-gold-500)]" />
                      {topic}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm leading-6 text-white/70">
                  Complete more practice questions to identify your weakest topics.
                </p>
              )}
            </div>

            <div className="bg-white p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--ibp-ink)]">
                <ClipboardCheck className="h-4 w-4 text-[#15803d]" /> Test-day checklist
              </h2>
              <ul className="mt-4 space-y-2 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
                <li>Confirm your appointment time and testing method.</li>
                <li>Review the current identification and check-in requirements.</li>
                <li>Know the testing rules and prohibited items before arriving.</li>
                <li>Plan your travel or online check-in buffer.</li>
              </ul>
              <a
                href={PSI_BULLETIN_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline"
              >
                Open the official PSI bulletin <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </section>

          <p className="mt-6 text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
            Readiness and practice targets are study guidance, not a prediction or guarantee of an exam result.
          </p>
        </>
      )}
    </StudyShell>
  );
}
